import React, { useState } from 'react';
import { Button, colors, Select, useWindowDimensions } from '@insurancemenu/imenu-components-lib';
import { ArrowBack, InfoButton, Cart, FilterList, Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { useQueryParams } from 'global/hooks/useQuery';
import { StandardFormChangeEvent } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { createStyles } from './styles';
import FilterModal from '../FilterModal';
import ProposalInfoModal from '../ProposalInfoModal';

export interface MobileBrowsingContentProps {
  browsingPlans: JSX.Element;
  compareAllButton: JSX.Element;
  currentProductLineId: ProductLineId;
  filter: JSX.Element;
  editableBlock: JSX.Element;
  portfoliosQuantity: number;
  resetData: () => void;
  onProductLineChange: (e: StandardFormChangeEvent) => void;
  proposalInfo: (additionalButton?: JSX.Element) => JSX.Element;
}

const MobileBrowsingContent = observer(
  ({
    resetData,
    currentProductLineId,
    browsingPlans,
    compareAllButton,
    editableBlock,
    filter,
    portfoliosQuantity,
    proposalInfo,
    onProductLineChange,
  }: MobileBrowsingContentProps): JSX.Element => {
    const history = useHistory();
    const query = useQueryParams();
    const [showFilter, setShowFilter] = useState(false);

    // Mobx store
    const {
      browsingStore: {
        productLines,
        cart,
        loading,
        areFiltersApplied,
        setProposalSidebarOpeningStatus,
        isProposalSidebarOpened,
      },
      domainStore: { getProductLineOptions },
    } = useRootStore();

    const convertedProductLines = productLines.map((line) => toJS(line)).slice();
    const convertedCartItems = cart.cartItems.map((item) => toJS(item)).slice();

    const classes = createStyles();

    const cartId = query.get('cartId') || null;
    const groupId = query.get('groupId') || '';

    // Show different layout for mobile and tablet view
    const { width: windowWidth } = useWindowDimensions();

    const onRedirect = (): void => {
      history.push(`/cart?groupId=${groupId}&cartId=${cartId}&cartType=browsing`);
    };

    const closeProposalInfoModal = (): void => {
      setProposalSidebarOpeningStatus(false);
    };

    const closeFilterModal = (): void => {
      setShowFilter(false);
    };

    const ProductLineSwitcher = (
      <div className={classes.productLineSwitcher}>
        <Select
          label="Product line"
          name="productLine"
          options={getProductLineOptions(convertedProductLines)}
          value={currentProductLineId || convertedProductLines[0]?.productLineId}
          onChange={onProductLineChange}
          width="25rem"
        />
        {/* Don't show reset button here because browsing page has it's own component.
        This button should be shown if that button is hidden */}
        {areFiltersApplied && portfoliosQuantity !== 0 ? (
          <Button
            color="custom"
            customColor={colors.red[600]}
            type="text"
            leftIconProp={<Delete />}
            className={classes.resetButton}
            onClick={resetData}
          >
            Reset Filters
          </Button>
        ) : (
          <></>
        )}
      </div>
    );

    return (
      <div>
        <div className={classes.mobileButtonsBar}>
          <Button
            leftIconProp={<ArrowBack />}
            type="text"
            size="medium"
            onClick={(): void => {
              resetData();
              history.push(
                `/popular-plans?groupId=${groupId}&productLineId=${currentProductLineId}&cartId=${cartId}&cartType=popular`,
              );
            }}
          >
            Back
          </Button>
          <Button
            rightIconProp={<FilterList />}
            type="text"
            size="medium"
            disabled={loading}
            className={classes.filterButton}
            onClick={(): void => {
              setShowFilter(true);
            }}
          >
            Filter
          </Button>
        </div>
        {currentProductLineId && (
          <h2 className={classes.title}>{`Browsing ${PRODUCT_LINES_CONTENT_MAP[currentProductLineId].label}`}</h2>
        )}
        {editableBlock}
        {compareAllButton}
        {ProductLineSwitcher}
        {browsingPlans}
        <FilterModal show={showFilter} onClose={closeFilterModal} loading={false} filter={filter} />
        <ProposalInfoModal
          show={isProposalSidebarOpened}
          onClose={closeProposalInfoModal}
          loading={loading}
          proposalContent={proposalInfo(
            <Button type="outlined" onClick={closeProposalInfoModal} size="large" color="primary">
              Close
            </Button>,
          )}
        />
        {!loading && (
          <div className={classes.actionButtons}>
            <Button
              leftIconProp={<InfoButton />}
              type="outlined"
              color="primary"
              size="large"
              onClick={(): void => {
                setProposalSidebarOpeningStatus(true);
              }}
              className={classes.proposalButton}
            >
              {windowWidth <= 524 ? 'Details' : 'Proposal Details'}
            </Button>
            <Button
              leftIconProp={<Cart />}
              type="contained"
              color="custom"
              customColor={colors.orange[600]}
              size="large"
              onClick={onRedirect}
            >
              {windowWidth <= 524 ? `Cart (${convertedCartItems.length})` : `Go to cart (${convertedCartItems.length})`}
            </Button>
          </div>
        )}
      </div>
    );
  },
);

export default MobileBrowsingContent;
